import {useEffect, useCallback, useMemo} from 'react';
import {useSelector, useDispatch} from 'react-redux';

import type {FilterObject} from 'nutshell-core/types';

import type {NutshellState} from '../../../store';

import {FullPageEntityListContainer} from '../../core/entity-list/full-page/full-page-entity-list-container';
import {fetchLeadsSchema} from '../../master-dashboard/actions/base';
// @ts-expect-error
import {getLeadsSchema} from '../../master-dashboard/dashboard-selectors';
import {requestListData as requestEngagementLeadsListData} from '../../reports/engagement/leads/leads-actions';
import {
    getListItems,
    getListIsLoading,
    getListIsFirstLoad,
    getListMeta,
    getListErrorMessage,
} from '../../reports/engagement/leads/leads-selectors';
import {
    DEFAULT_FIELDS_ENGAGEMENT_BAR_LEADS,
    DEFAULT_SORT_FIELD_ENGAGEMENT_BAR_LEADS,
} from '../../reports/report-constants';

import {EngagementBarLeadsEmptyState} from '../chat/engagement-bar-leads-empty-state';

interface Props {
    filters: FilterObject[];
    selectedPage: number;
    selectedSortField: string;
    selectedFields: string[];
    searchQuery?: string;
    onParamChange: (params: any, replace: boolean) => void;
}

export function EngagementBarLeadsListContainer(props: Props) {
    const dispatch = useDispatch();

    const schema = useSelector((state: NutshellState) => getLeadsSchema(state));
    const listItems = useSelector(getListItems);
    const isLoading = useSelector(getListIsLoading);
    const isFirstLoad = useSelector(getListIsFirstLoad);
    const listMeta = useSelector(getListMeta);
    const errorMessage = useSelector(getListErrorMessage);

    const count = listMeta?.count ?? 0;

    const memoizedFields = useMemo(
        () =>
            props.selectedFields.length > 0
                ? props.selectedFields
                : [DEFAULT_FIELDS_ENGAGEMENT_BAR_LEADS],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.selectedFields.join(',')]
    );

    const serializedFilters = JSON.stringify(props.filters);
    const memoizedFilters = useMemo(
        () => props.filters,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [serializedFilters]
    );

    const fetchList = useCallback(
        (
            filters: FilterObject[],
            pageNum: number,
            columns: string[],
            sort: string,
            search?: string
        ) => {
            dispatch(
                requestEngagementLeadsListData({
                    filters,
                    pageNum,
                    columns,
                    sort,
                    search,
                })
            );
        },
        [dispatch]
    );

    useEffect(() => {
        dispatch(fetchLeadsSchema() as any);
    }, [dispatch]);

    useEffect(() => {
        fetchList(
            memoizedFilters,
            props.selectedPage,
            memoizedFields,
            props.selectedSortField || DEFAULT_SORT_FIELD_ENGAGEMENT_BAR_LEADS,
            props.searchQuery
        );
    }, [
        fetchList,
        memoizedFilters,
        props.selectedPage,
        memoizedFields,
        props.selectedSortField,
        props.searchQuery,
    ]);

    return (
        <FullPageEntityListContainer
            filters={memoizedFilters}
            defaultFields={[DEFAULT_FIELDS_ENGAGEMENT_BAR_LEADS]}
            selectedPage={props.selectedPage}
            selectedSortField={props.selectedSortField || DEFAULT_SORT_FIELD_ENGAGEMENT_BAR_LEADS}
            selectedFields={memoizedFields}
            onParamChange={props.onParamChange}
            schema={schema}
            listItems={listItems}
            count={count}
            unfilteredCount={isFirstLoad ? undefined : count}
            unfilteredCountThreshold={0}
            entityType='leads'
            scrollContainerSelector='#engagement-bar-leads-list'
            isFirstLoad={isFirstLoad}
            isLoading={isLoading}
            hasError={Boolean(errorMessage)}
            includeColumnPicker={true}
            onRemoveAllFilters={() => {}}
            customEmptyStateComponent={
                <EngagementBarLeadsEmptyState
                    hasFilters={Boolean(memoizedFilters.length)}
                    onRemoveFilters={() =>
                        props.onParamChange({filter: {}, q: undefined, list: {page: 1}}, false)
                    }
                />
            }
            checkRowSelected={() => false}
            isAllSelected={false}
            footerHeight={0}
            isFilterDrawerOpen={false}
        />
    );
}
